import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import {
    C10S_SITE_LABEL, C10sOptions, C10sProductRow, C10sSite, C10sSiteHealth, C10sStats,
    getC10sHealth, getC10sOptions, getC10sStats, listC10sProducts,
} from '../../api/assets';
import { ApiError } from '../../api/request';
import ImageLightbox from '../../components/ImageLightbox';
import C10sOrders from './C10sOrders';
import Pagination from '../../components/Pagination';
import { useSyncQueryToUrl } from '../../utils/urlState';
import { useSearchInput } from '../../utils/useSearchInput';
import { MerchantBadge, ParamBadge, TextureChips, areaText, areaTextWithPos, fmtUnix, processTechText, storeTypeText } from './c10sLabels';
import { DataSourceBar, NoticeBar, SegGroup, StatCard } from './parts';
import { ProductImg } from './productLabels';
import SearchSelect from '../../components/SearchSelect';

// 数据资产·c10s 商家数据中心 (2026-09-09, 需求 wiki 第 2 节):
// c10s (customthings 定制商城) 商家上架的可定制商品 —— 底图、可设计区域 (形状 宽×高 mm;
// 商品开了 enable_mulit_area 时一个面可以划多个区域, 取 printAreaArr)、
// 加工参数 (部分商家提供). 企业级商家 (华为/Nike/HydroJug…) 单独打标.
// 材质取自 **side_designs[].texture.id → t_texture 字典** (2026-09-10 用户澄清; 不是
// t_product.sides[].material —— 那个字段商家基本不填, 美服生产 14,574 商品里只有 32 个有值).
// **实时直读 c10s 源库, 不落库不同步** —— 与耗材识别/加工数据两个中心同一套做法.
// 只含 source_channel=customthings 的商品: 其余渠道是第三方店铺同步来的, 没有这些字段.

const PAGE_SIZE = 24;

/** URL 上的 site 参数 → 站点 (非法值当美服; 后端也会校验) */
function siteFromQuery(v: string | null): C10sSite {
    return v === 'cn' ? 'cn' : 'us';
}

/** 两个 tab: 商品 (默认) / 订单. 订单 tab 可带 ?productId= 收窄到单个商品 */
type Tab = 'products' | 'orders';
function tabFromQuery(v: string | null): Tab {
    return v === 'orders' ? 'orders' : 'products';
}

function C10sList() {
    const history = useHistory();
    const initQ = new URLSearchParams(useLocation().search);
    // 数据源站点: 美服 / 国服各一份 c10s 数据 (表结构一样), 切换等于换库重查.
    const [site, setSite] = useState<C10sSite>(siteFromQuery(initQ.get('site')));
    const [view, setView] = useState<'grid' | 'table'>(initQ.get('view') === 'grid' ? 'grid' : 'table');
    const [merchantKind, setMerchantKind] = useState(initQ.get('merchantKind') || '');
    const [hasParam, setHasParam] = useState(initQ.get('hasParam') || '');
    const [enterpriseId, setEnterpriseId] = useState(initQ.get('enterpriseId') || '');
    const [textureId, setTextureId] = useState(initQ.get('textureId') || '');
    // 关键词「草稿 → 提交」: 见 useSearchInput —— 这里的搜索同样是跨表 LIKE, 不能边打边搜.
    const kw = useSearchInput(initQ.get('keyword') || '', () => setPage(1));
    const keyword = kw.value;
    const [page, setPage] = useState(Number(initQ.get('page')) || 1);
    // 订单 tab: productId 为空 = 全站订单; 商品列表/详情的「订单 N」入口会带上它跳过来.
    const [tab, setTab] = useState<Tab>(tabFromQuery(initQ.get('tab')));
    const [orderProductId, setOrderProductId] = useState(initQ.get('productId') || '');
    const [orderProductLabel, setOrderProductLabel] = useState(initQ.get('productTitle') || '');

    const [list, setList] = useState<C10sProductRow[]>([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [stats, setStats] = useState<C10sStats | null>(null);
    // 列表这一批筛选是否已经拿到结果 —— 统计要等它 (见下面的 effect)
    const [listReady, setListReady] = useState(false);
    const [options, setOptions] = useState<C10sOptions | null>(null);
    const [health, setHealth] = useState<{ enabled: boolean; ok?: boolean; error?: string; sites?: C10sSiteHealth[] } | null>(null);
    const [zoomSrc, setZoomSrc] = useState<string | null>(null);

    useSyncQueryToUrl('/assets/c10s-products', {
        site: site === 'us' ? '' : site,
        view: view === 'table' ? '' : view, merchantKind, hasParam, enterpriseId, textureId, keyword, page,
        tab: tab === 'products' ? '' : tab,
        productId: tab === 'orders' ? orderProductId : '',
        productTitle: tab === 'orders' ? orderProductLabel : '',
    });

    const filterParams = useMemo(() => ({
        site,
        merchantKind: merchantKind || undefined, hasParam: hasParam || undefined,
        enterpriseId: enterpriseId || undefined, textureId: textureId || undefined, keyword: keyword || undefined,
    }), [site, merchantKind, hasParam, enterpriseId, textureId, keyword]);

    const load = useCallback(async () => {
        setLoading(true);
        setError('');
        try {
            const res = await listC10sProducts({ ...filterParams, page, pageSize: PAGE_SIZE });
            setList(res.list || []);
            setTotal(res.total);
        } catch (e) {
            setError(e instanceof ApiError ? e.message : String(e));
            setList([]);
            setTotal(0);
        } finally {
            setLoading(false);
            setListReady(true);
        }
    }, [filterParams, page]);

    // 统计条与列表用同一批筛选; 商家类型不参与统计 (三个数是同一批商品的拆分, 后端已按此口径算).
    const loadStats = useCallback(async () => {
        try {
            setStats(await getC10sStats(filterParams));
        } catch {
            setStats(null);
        }
    }, [filterParams]);

    useEffect(() => { if (tab === 'products') load(); }, [load, tab]);
    // 换筛选 → 统计作废, 并重新等列表 (翻页不动 listReady, 所以翻页不会重复拉统计).
    useEffect(() => { setListReady(false); setStats(null); }, [filterParams]);
    // 统计排在列表之后: 它是跨表 count, 与列表并发会抢源库连接, 让列表先出 (2026-09-20).
    useEffect(() => { if (tab === 'products' && listReady) loadStats(); }, [loadStats, tab, listReady]);
    // 企业/材质候选是按站点来的 (两边的 store 与 t_texture 各自独立), 换站点要重新拉.
    useEffect(() => {
        getC10sOptions(site).then(setOptions).catch(() => setOptions(null));
    }, [site]);
    useEffect(() => { getC10sHealth().then(setHealth).catch(() => undefined); }, []);

    // 换站点: 企业/材质 id 在另一个站点没有意义, 连同分页一起清掉 (商家类型/参数/关键词留着).
    const switchSite = (v: string) => {
        const next = siteFromQuery(v);
        if (next === site) return;
        setSite(next);
        setEnterpriseId(''); setTextureId(''); setPage(1);
    };
    const siteHealth = (health?.sites || []).find((x) => x.site === site);
    const siteOptions = (health?.sites || []).filter((x) => x.enabled || x.site === 'us')
        .map((x) => ({ value: x.site, label: x.label || C10S_SITE_LABEL[x.site] }));

    const resetFilters = () => {
        setMerchantKind(''); setHasParam(''); setEnterpriseId(''); setTextureId('');
        kw.setDraft(''); setPage(1); // 清空草稿即提交空关键词 (见 useSearchInput)
    };

    const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
    const filtered = Boolean(merchantKind || hasParam || enterpriseId || textureId || keyword);
    // 详情页要知道看的是哪个站点的库, site 跟着一起带过去.
    const goDetail = (id: string) => history.push(`/assets/c10s-products/${id}${site === 'us' ? '' : `?site=${site}`}`);
    // 「订单 N」: 切到订单 tab 并收窄到这个商品 (URL 同步由 useSyncQueryToUrl 负责, 刷新/分享都还在).
    const goOrders = (p: C10sProductRow) => {
        setOrderProductId(p.id);
        setOrderProductLabel(p.title || '');
        setTab('orders');
    };

    return (
        <div className="space-y-5">
            {/* 页头: 标题与说明并排一行 —— 表格才是主角, 页头不占两三行高度 (2026-09-20) */}
            <div className="flex items-center justify-between gap-4 flex-wrap">
                <div className="flex items-baseline gap-2 min-w-0">
                    <div className="text-lg font-extrabold text-slate-900 tracking-tight shrink-0">商家数据</div>
                    <div className="text-xs text-slate-400 truncate">
                        c10s 定制商城商家上架的可定制商品：底图 · 可设计区域 · 加工参数 · 材质
                    </div>
                </div>
                <DataSourceBar health={siteHealth ? { enabled: siteHealth.enabled, ok: siteHealth.ok, error: siteHealth.error } : health}
                    source={`c10s ${C10S_SITE_LABEL[site]}源库 (${siteHealth?.db || 'customthings-service'})`} />
            </div>

            {/* tab: 商品 (商家上架了什么) / 订单 (买家实际下单加工了什么).
                做成有底色的分段开关而不是下划线 tab —— 下划线那版看不出是两个可切换的视图 (2026-09-20). */}
            <div className="flex items-center gap-3 flex-wrap">
                <div className="inline-flex items-center gap-1 rounded-lg bg-slate-100 p-1" role="tablist">
                    {([['products', '商品', '商家上架的可定制商品'],
                       ['orders', '订单', '买家下单的定制结果：设计图 + 下单时的加工参数快照']] as const).map(([key, label, hint]) => (
                        <button key={key} type="button" role="tab" aria-selected={tab === key} title={hint}
                            onClick={() => setTab(key)}
                            className={`rounded-md px-5 py-1.5 text-sm transition ${tab === key
                                ? 'bg-white text-slate-900 font-semibold shadow-sm ring-1 ring-slate-200'
                                : 'text-slate-500 hover:text-slate-800 hover:bg-white/60'}`}>
                            {label}
                        </button>
                    ))}
                </div>
                <span className="text-xs text-slate-400">
                    {tab === 'products'
                        ? '当前看：商家上架的可定制商品 · 点「订单」看买家实际下单的定制结果'
                        : '当前看：买家下单的定制结果 (设计图 + 加工参数快照) · 点「商品」看商家上架的商品'}
                </span>
            </div>

            {/* 统计条: 商品总数 · 企业级 · 小商家 (需求原文三项) + 带参数/覆盖店铺 */}
            {tab === 'products' && <div className="grid grid-cols-5 gap-4">
                <StatCard label="商品总数" value={stats ? stats.total.toLocaleString() : '—'}
                    sub={filtered ? '当前筛选下' : '仅 customthings 渠道'} />
                <StatCard label="企业级" value={stats ? stats.enterprise.toLocaleString() : '—'}
                    sub="企业总店 + 企业分店" accent="text-indigo-600" />
                <StatCard label="小商家" value={stats ? stats.personal.toLocaleString() : '—'}
                    sub="个人店铺" accent="text-slate-600" />
                <StatCard label="带加工参数" value={stats ? stats.withParam.toLocaleString() : '—'}
                    sub="算法可直接取的样本" accent="text-teal-600" />
                <StatCard label="覆盖店铺" value={stats ? stats.storeCount.toLocaleString() : '—'} sub="按店铺去重" />
            </div>}

            {/* 提示只留"看这张表需要知道的"两句; 源侧表名/字段名这类实现细节挪到列头 tooltip 与实施记录 */}
            {tab === 'products' ? (
                <NoticeBar>
                    <b>企业级</b> = 企业总店 / 分店（显示企业名），<b>小商家</b> = 个人店铺。
                    加工参数只标<b>有 / 无</b>，具体功率·速度·次数在详情里看。
                    商品有订单时，列表与详情都有<b>「订单 N」</b>入口，点进去就是这个商品的订单。
                </NoticeBar>
            ) : (
                <NoticeBar>
                    一格 = 订单里的<b>一行商品</b>，图是买家定制后的<b>设计图</b>。
                    点一格看那一单<b>下单时的加工参数快照</b>（源侧写成全零占位的已过滤，显示为"没有参数快照"）。
                </NoticeBar>
            )}

            {/* 筛选 */}
            <div className="card px-4 py-3 space-y-2.5">
                {siteOptions.length > 1 && (
                    <div className="flex flex-wrap items-center gap-x-5 gap-y-2 pb-2.5 border-b border-slate-50">
                        <SegGroup label="数据源" value={site} onChange={switchSite} options={siteOptions}
                            title="美服与国服各有一份 c10s 数据 (两个库, 表结构一样); 切换即换库重查" />
                        <span className="text-[11px] text-slate-400">
                            {siteHealth?.ok === false
                                ? `⚠ ${C10S_SITE_LABEL[site]}源库连不上：${siteHealth.error || '未知错误'}`
                                : `当前看的是 ${C10S_SITE_LABEL[site]}的 c10s 库${siteHealth?.db ? ` (${siteHealth.db})` : ''}`}
                        </span>
                    </div>
                )}
                <div className="flex flex-wrap items-center gap-x-5 gap-y-2">
                    <SegGroup label="商家类型" value={merchantKind} onChange={(v) => { setMerchantKind(v); setPage(1); }}
                        options={[{ value: '', label: '全部' }, { value: 'enterprise', label: '企业级' }, { value: 'personal', label: '小商家' }]} />
                    {tab === 'products' && (
                        <SegGroup label="加工参数" value={hasParam} onChange={(v) => { setHasParam(v); setPage(1); }}
                            options={[{ value: '', label: '全部' }, { value: 'yes', label: '有' }, { value: 'no', label: '无' }]} />
                    )}
                    <button type="button" className="ml-auto text-xs text-blue-600 hover:bg-blue-50 px-2 py-1 rounded" onClick={resetFilters}>清空</button>
                </div>
                <div className="flex flex-wrap items-center gap-2 pt-2.5 border-t border-slate-50">
                    <SearchSelect className="w-56" value={enterpriseId} onChange={(v) => { setEnterpriseId(v); setPage(1); }}
                        placeholder="全部企业" clearable
                        options={(options?.enterprises || []).map((e) => ({
                            value: String(e.enterpriseId),
                            label: `${e.name || e.enterpriseId} · ${e.products.toLocaleString()}`,
                        }))} />
                    <SearchSelect className="w-52" value={textureId} onChange={(v) => { setTextureId(v); setPage(1); }}
                        placeholder="全部材质" clearable
                        options={(options?.textures || []).map((t) => ({
                            value: String(t.id),
                            label: `${t.title || `材质 #${t.id}`}${t.status === 'offline' ? '（下线）' : ''} · ${t.products.toLocaleString()}`,
                        }))} />
                    <input className="inp" style={{ width: 240 }}
                        placeholder={tab === 'products' ? '商品标题 / 商家名 / SKU…（回车搜索）' : '按商品标题 / 商家名 / SKU 筛订单…（回车搜索）'}
                        value={kw.draft}
                        onChange={(e) => kw.setDraft(e.target.value)}
                        onKeyDown={kw.onKeyDown}
                        onBlur={kw.commit} />
                    <div className="ml-auto flex items-center gap-1 text-xs" hidden={tab !== 'products'}>
                        {([['table', '表格'], ['grid', '图墙']] as const).map(([key, label]) => (
                            <button key={key} type="button"
                                className={`px-2.5 py-1 rounded-md ${view === key ? 'bg-slate-200 text-slate-800 font-semibold' : 'text-slate-500 hover:bg-slate-100'}`}
                                onClick={() => setView(key)}>{label}</button>
                        ))}
                    </div>
                </div>
            </div>

            {error && <div className="card p-3 text-sm text-rose-600 border-rose-200 bg-rose-50">错误: {error}</div>}

            {tab === 'orders' ? (
                <C10sOrders site={site} productId={orderProductId} productLabel={orderProductLabel}
                    productFilter={{ keyword, textureId, merchantKind, enterpriseId }}
                    onClearProduct={() => { setOrderProductId(''); setOrderProductLabel(''); }}
                    onZoom={setZoomSrc} />
            ) : (
            <div className="card">
                <div className="flex items-center px-4 pt-3 pb-2">
                    <div className="text-sm font-semibold text-slate-700">商品明细</div>
                    <div className="ml-2 text-xs text-slate-400">{filtered ? '已筛选' : '全部商品'} · 按源侧更新时间倒序</div>
                    <div className="ml-auto text-xs text-slate-400">共 {total.toLocaleString()} 件</div>
                </div>
                {loading ? (
                    <div className="text-center text-slate-400 py-12 text-sm">加载中…</div>
                ) : list.length === 0 ? (
                    <div className="text-center text-slate-400 py-12 text-sm">没有符合条件的商品</div>
                ) : view === 'grid' ? (
                    <div className="grid gap-3 px-4 pb-4" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))' }}>
                        {list.map((p) => {
                            const main = p.baseImages[0] || p.thumbnailUrl;
                            return (
                                <div key={p.id} className="group rounded-xl border border-slate-100 overflow-hidden hover:shadow-md hover:border-slate-200 transition cursor-pointer bg-white flex flex-col"
                                    onClick={() => goDetail(p.id)}>
                                    <div className="relative bg-white border-b border-slate-50" style={{ aspectRatio: '1 / 1' }}>
                                        {main ? (
                                            <ProductImg src={main} className="w-full h-full object-contain p-2"
                                                onClick={(e) => { e.stopPropagation(); setZoomSrc(main); }} />
                                        ) : (
                                            <div className="w-full h-full flex items-center justify-center text-[11px] text-slate-400 bg-slate-50">无底图</div>
                                        )}
                                        <div className="absolute top-1.5 left-1.5"><MerchantBadge kind={p.merchant.kind} /></div>
                                        {p.baseImages.length > 1 && (
                                            <div className="absolute bottom-1.5 right-1.5 px-1.5 py-0.5 rounded bg-black/50 text-white text-[10px]">{p.baseImages.length} 图</div>
                                        )}
                                    </div>
                                    <div className="p-2.5 flex-1 flex flex-col">
                                        <div className="text-[12px] text-slate-800 leading-snug" title={p.title}
                                            style={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden', minHeight: 34 }}>
                                            {p.title || <span className="text-slate-400">无标题</span>}
                                        </div>
                                        <div className="mt-1.5 text-[11px] text-slate-500 truncate" title={p.merchant.storeName}>{p.merchant.storeName || '—'}</div>
                                        <div className="mt-1.5 flex items-center gap-1.5">
                                            <ParamBadge has={p.hasParam} modes={p.paramModes} />
                                            <span className="text-[11px] text-slate-400">{p.skuCount} SKU</span>
                                            {(p.orderCount || 0) > 0 && (
                                                <button type="button" className="ml-auto text-[11px] text-blue-600 hover:underline"
                                                    title="看这个商品的订单"
                                                    onClick={(e) => { e.stopPropagation(); goOrders(p); }}>
                                                    订单 {p.orderCount}
                                                </button>
                                            )}
                                        </div>
                                        <div className="mt-1.5 min-h-[20px]"><TextureChips items={p.textures} max={2} /></div>
                                        <div className="mt-auto pt-1.5 text-[10px] text-slate-400 truncate" title={p.areas.map(areaText).join(' · ')}>
                                            {p.areas.length > 0 ? areaText(p.areas[0]) : '未设可设计区域'}
                                            {p.areas.length > 1 && ` +${p.areas.length - 1}`}
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full text-[13px]" style={{ minWidth: 1120 }}>
                            <thead>
                                <tr className="text-left text-[10px] font-extrabold uppercase text-slate-400 border-b border-slate-100" style={{ letterSpacing: '1.5px', background: 'rgb(248 250 252 / 60%)' }}>
                                    <th className="px-3 py-2.5 whitespace-nowrap" title="企业级 = 店铺类型 enterprise / branch, 显示企业名; 小商家 = personal, 显示店铺名">商家</th>
                                    <th className="px-3 py-2.5 whitespace-nowrap" style={{ minWidth: 190 }}>产品标题</th>
                                    <th className="px-3 py-2.5 whitespace-nowrap">SKU</th>
                                    <th className="px-3 py-2.5 whitespace-nowrap">底图</th>
                                    <th className="px-3 py-2.5 whitespace-nowrap" title="多区域商品 (enable_mulit_area) 取各面 printAreaArr, 否则取 printArea">可设计区域</th>
                                    <th className="px-3 py-2.5 whitespace-nowrap" title="有 = 变体绑了参数组或面上配了参数; 具体数值在详情里">加工参数</th>
                                    <th className="px-3 py-2.5 whitespace-nowrap" title="取自规格各面选的材质 (side_designs.texture → t_texture 字典); 商家自填的 sides.material 基本没人填, 页面不用">材质</th>
                                    {/* 直读模式没有"同步"动作, 所以不叫需求原文的「同步时间」: 这就是源库这一行的 updated_at */}
                                    <th className="px-3 py-2.5 whitespace-nowrap" title="源侧 t_product.updated_at —— 实时直读, 没有同步动作">更新时间</th>
                                    {/* 操作列吸附右侧: 表格比容器宽时 (材质/底图一多就会), 详情入口不会被挤出可视区 */}
                                    <th className="px-3 py-2.5 text-right whitespace-nowrap sticky right-0 z-10" style={{ background: 'rgb(248 250 252)' }}>操作</th>
                                </tr>
                            </thead>
                            <tbody>
                                {list.map((p) => (
                                    <tr key={p.id} className="group border-b border-slate-50 cursor-pointer hover:bg-slate-50" onClick={() => goDetail(p.id)}>
                                        <td className="px-3 py-2 max-w-[170px]">
                                            <div className="flex items-center gap-1.5">
                                                <MerchantBadge kind={p.merchant.kind} />
                                                <span className="text-slate-700 truncate" title={p.merchant.storeName}>{p.merchant.storeName || '—'}</span>
                                            </div>
                                            <div className="text-[11px] text-slate-400 truncate" title={p.merchant.shopName}>
                                                {storeTypeText(p.merchant.storeType)}
                                                {p.merchant.kind === 'enterprise' && p.merchant.shopName && p.merchant.shopName !== p.merchant.storeName
                                                    ? ` · ${p.merchant.shopName}` : ''}
                                            </div>
                                        </td>
                                        <td className="px-3 py-2">
                                            <div className="text-slate-700 break-words max-w-[220px]" title={p.title}
                                                style={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                                                {p.title || <span className="text-slate-400">无标题</span>}
                                            </div>
                                            <div className="text-[11px] text-slate-400">{processTechText(p.processTech)}{p.devices.length > 0 ? ` · ${p.devices.join('/')}` : ''}</div>
                                        </td>
                                        <td className="px-3 py-2 font-mono text-[11px] text-slate-500 whitespace-nowrap max-w-[130px] truncate" title={p.skus.join(', ')}>
                                            {p.skus[0] || <span className="text-slate-300">—</span>}
                                            {p.skuCount > 1 && <span className="text-slate-400"> +{p.skuCount - 1}</span>}
                                        </td>
                                        <td className="px-3 py-2">
                                            <div className="flex items-center gap-1">
                                                {p.baseImages.slice(0, 3).map((u) => (
                                                    <ProductImg key={u} src={u} className="rounded-md object-contain bg-white ring-1 ring-slate-100 cursor-zoom-in"
                                                        style={{ width: 36, height: 36, minWidth: 36 }}
                                                        onClick={(e) => { e.stopPropagation(); setZoomSrc(u); }} />
                                                ))}
                                                {p.baseImages.length > 3 && <span className="text-[11px] text-slate-400 whitespace-nowrap">+{p.baseImages.length - 3}</span>}
                                                {p.baseImages.length === 0 && <span className="text-[11px] text-slate-300">—</span>}
                                            </div>
                                        </td>
                                        <td className="px-3 py-2 text-[12px] text-slate-600 max-w-[150px]">
                                            {p.areas.length === 0 ? <span className="text-slate-300">—</span> : (
                                                <>
                                                    <div className="truncate" title={p.areas.map(areaTextWithPos).join('\n')}>{areaText(p.areas[0])}</div>
                                                    {p.areas.length > 1 && (
                                                        <div className="text-[11px] text-slate-400">
                                                            另 {p.areas.length - 1} 个区域{p.enableMultiArea ? '（多区域）' : ''}
                                                        </div>
                                                    )}
                                                </>
                                            )}
                                        </td>
                                        <td className="px-3 py-2"><ParamBadge has={p.hasParam} modes={p.paramModes} /></td>
                                        <td className="px-3 py-2 max-w-[140px]"><TextureChips items={p.textures} max={2} /></td>
                                        {/* 更新时间拆两行: 表格已经贴着容器宽度, 单行日期时间会把右侧吸附的操作列压住 */}
                                        <td className="px-3 py-2 text-[11px] text-slate-500 whitespace-nowrap leading-tight">
                                            <div>{fmtUnix(p.updatedAt).slice(0, 10)}</div>
                                            <div className="text-slate-400">{fmtUnix(p.updatedAt).slice(11)}</div>
                                        </td>
                                        <td className="px-3 py-2 text-right whitespace-nowrap text-xs sticky right-0 bg-white group-hover:bg-slate-50 shadow-[-6px_0_6px_-6px_rgba(15,23,42,.12)]">
                                            <button type="button" className="text-blue-600 hover:underline" onClick={(e) => { e.stopPropagation(); goDetail(p.id); }}>详情</button>
                                            {(p.orderCount || 0) > 0 && (
                                                <button type="button" className="ml-2 text-blue-600 hover:underline"
                                                    title="看这个商品的订单 (切到订单 tab 并按它筛选)"
                                                    onClick={(e) => { e.stopPropagation(); goOrders(p); }}>
                                                    订单 {p.orderCount}
                                                </button>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
                {totalPages > 1 && (
                    <div className="px-4 py-3 border-t border-slate-100 flex justify-end">
                        <Pagination page={page} totalPages={totalPages} onChange={setPage} total={total} />
                    </div>
                )}
            </div>
            )}
            <ImageLightbox src={zoomSrc} onClose={() => setZoomSrc(null)} />
        </div>
    );
}

export default C10sList;
